import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../common/Toast';

const PaymentForm = () => {
  const { user, createPayment } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    country: 'Sénégal',
    city: '',
    payment_method: 'orange_money',
    amount: 0,
    plan: ''
  });

  const [errors, setErrors] = useState({});

  // Récupérer les informations de la transaction depuis la session
  useEffect(() => {
    const pendingTransaction = sessionStorage.getItem('aeii_pending_payment');
    if (pendingTransaction) {
      try {
        const data = JSON.parse(pendingTransaction);
        setFormData(prev => ({
          ...prev,
          plan: data.plan || '',
          amount: data.amount || 0,
          email: user?.email || '',
          full_name: user?.full_name || ''
        }));
        console.log('📦 Transaction chargée:', data);
      } catch (e) {
        console.error('Erreur chargement transaction:', e);
        navigate('/abonnements');
      }
    } else {
      // Rediriger si aucune transaction en cours
      console.warn('⚠️ Aucune transaction en cours');
      navigate('/abonnements');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Effacer l'erreur du champ
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Le nom complet est requis';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Le numéro de téléphone est requis';
    } else if (formData.phone.length < 8) {
      newErrors.phone = 'Numéro de téléphone invalide';
    }
    
    if (!formData.country.trim()) {
      newErrors.country = 'Le pays est requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showToast('Veuillez corriger les erreurs du formulaire', 'error');
      return;
    }

    setLoading(true);

    try {
      // Sauvegarder les coordonnées du client
      sessionStorage.setItem('aeii_customer_info', JSON.stringify({
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        city: formData.city,
        payment_method: formData.payment_method
      }));

      // Créer la transaction de paiement
      console.log('🔄 Création du paiement pour:', formData.plan);
      const paymentData = await createPayment(formData.plan);
      
      console.log('📦 Données de paiement reçues:', paymentData);

      // Sauvegarder l'URL de paiement dans la session
      if (paymentData.paymentUrl) {
        sessionStorage.setItem('aeii_payment_url', paymentData.paymentUrl);
        sessionStorage.setItem('aeii_transaction_id', paymentData.transactionId);
        console.log('✅ URL de paiement sauvegardée:', paymentData.paymentUrl);
      } else {
        console.warn('⚠️ Pas d\'URL de paiement reçue');
      }

      // Mettre à jour la session avec les coordonnées
      const pendingTransaction = JSON.parse(sessionStorage.getItem('aeii_pending_payment') || '{}');
      sessionStorage.setItem('aeii_pending_payment', JSON.stringify({
        ...pendingTransaction,
        paymentUrl: paymentData.paymentUrl,
        transactionId: paymentData.transactionId,
        customer_info: {
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          country: formData.country,
          city: formData.city,
          payment_method: formData.payment_method
        }
      }));

      // Rediriger vers la page de traitement
      navigate('/payment-processing', { 
        state: { 
          paymentUrl: paymentData.paymentUrl,
          transactionId: paymentData.transactionId,
          plan: formData.plan,
          amount: formData.amount,
          mode: paymentData.mode || 'simulation'
        }
      });

    } catch (error) {
      console.error('❌ Erreur paiement:', error);
      showToast(error.message || 'Erreur lors de la création du paiement', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getPlanLabel = (plan) => {
    const labels = {
      classique: 'Classique',
      premium: 'Premium'
    };
    return labels[plan] || plan;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      orange_money: 'Orange Money',
      mtn_money: 'MTN Mobile Money',
      moov_money: 'Moov Money',
      wave: 'Wave',
      carte_bancaire: 'Carte bancaire'
    };
    return labels[method] || method;
  };

  return (
    <section className="section">
      <div className="container" style={{ maxWidth: '600px', margin: '0 auto' }}>
        <div style={{ 
          background: '#fff', 
          borderRadius: '16px', 
          padding: '40px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.1)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <span className="section-kicker" style={{ justifyContent: 'center' }}>
              <i className="fa-solid fa-credit-card"></i> Paiement sécurisé
            </span>
            <h2 className="section-title">Confirmez vos coordonnées</h2>
            <p style={{ color: '#667180' }}>
              Abonnement <strong>{getPlanLabel(formData.plan)}</strong> - {formData.amount.toLocaleString()} FCFA
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Nom complet */}
            <div className="form-group">
              <label htmlFor="full_name">Nom complet *</label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="Ex: Jean Dupont"
                className={errors.full_name ? 'error' : ''}
                style={errors.full_name ? { borderColor: '#e53e3e' } : {}}
              />
              {errors.full_name && (
                <span style={{ color: '#e53e3e', fontSize: '0.85rem' }}>{errors.full_name}</span>
              )}
            </div>

            {/* Email */}
            <div className="form-group">
              <label htmlFor="email">Adresse email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="exemple@email.com"
                className={errors.email ? 'error' : ''}
                style={errors.email ? { borderColor: '#e53e3e' } : {}}
              />
              {errors.email && (
                <span style={{ color: '#e53e3e', fontSize: '0.85rem' }}>{errors.email}</span>
              )}
            </div>

            {/* Téléphone */}
            <div className="form-group">
              <label htmlFor="phone">Numéro de téléphone *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="77 123 45 67"
                className={errors.phone ? 'error' : ''}
                style={errors.phone ? { borderColor: '#e53e3e' } : {}}
              />
              {errors.phone && (
                <span style={{ color: '#e53e3e', fontSize: '0.85rem' }}>{errors.phone}</span>
              )}
              <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '4px' }}>
                <i className="fa-solid fa-info-circle"></i> Numéro utilisé pour le paiement Mobile Money
              </div>
            </div>

            {/* Pays et Ville */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="country">Pays *</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={errors.country ? 'error' : ''}
                  style={errors.country ? { borderColor: '#e53e3e' } : {}}
                >
                  <option value="Sénégal">Sénégal</option>
                  <option value="Côte d'Ivoire">Côte d'Ivoire</option>
                  <option value="Cameroun">Cameroun</option>
                  <option value="Mali">Mali</option>
                  <option value="Burkina Faso">Burkina Faso</option>
                  <option value="Bénin">Bénin</option>
                  <option value="Togo">Togo</option>
                  <option value="Guinée">Guinée</option>
                  <option value="RDC">République Démocratique du Congo</option>
                  <option value="Autre">Autre</option>
                </select>
                {errors.country && (
                  <span style={{ color: '#e53e3e', fontSize: '0.85rem' }}>{errors.country}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="city">Ville</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Dakar, Abidjan..."
                />
              </div>
            </div>

            {/* Moyen de paiement */}
            <div className="form-group">
              <label htmlFor="payment_method">Moyen de paiement</label>
              <select
                id="payment_method"
                name="payment_method"
                value={formData.payment_method}
                onChange={handleChange}
              >
                <option value="orange_money">Orange Money</option>
                <option value="mtn_money">MTN Mobile Money</option>
                <option value="moov_money">Moov Money</option>
                <option value="wave">Wave</option>
                <option value="carte_bancaire">Carte bancaire</option>
              </select>
            </div>

            {/* Résumé de la commande */}
            <div style={{ 
              background: '#f7fafc', 
              padding: '16px', 
              borderRadius: '8px',
              margin: '16px 0',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#4a5568' }}>Abonnement</span>
                <span style={{ fontWeight: '600' }}>{getPlanLabel(formData.plan)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#4a5568' }}>Montant</span>
                <span style={{ fontWeight: '700', color: '#0c2340' }}>
                  {formData.amount.toLocaleString()} FCFA
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                <span style={{ color: '#4a5568' }}>Moyen de paiement</span>
                <span style={{ fontWeight: '500' }}>{getPaymentMethodLabel(formData.payment_method)}</span>
              </div>
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <Link to="/abonnements" className="btn btn-ghost" style={{ flex: 1 }}>
                <i className="fa-solid fa-arrow-left"></i> Retour
              </Link>
              <button 
                type="submit" 
                className="btn btn-primary" 
                style={{ flex: 2 }}
                disabled={loading}
              >
                {loading ? (
                  <><i className="fa-solid fa-spinner fa-spin"></i> Traitement...</>
                ) : (
                  <>Payer {formData.amount.toLocaleString()} FCFA <i className="fa-solid fa-arrow-right"></i></>
                )}
              </button>
            </div>

            <div style={{ 
              marginTop: '16px', 
              fontSize: '0.75rem', 
              color: '#a0aec0', 
              textAlign: 'center' 
            }}>
              <i className="fa-solid fa-lock"></i> Paiement sécurisé via Monetbil
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default PaymentForm;